/*
 * <BeTextControl> / <BeTextView> -- React wrappers for the .be-text-field CSS
 * component: the BeOS R5 text field (sunken white well, focus = nav-blue rect).
 * Reconstructed from
 * rdef_src/libbe_r5/reconstructed/BTextControl.Draw.match.cpp; see
 * components/text-control.scss for provenance.
 *
 *   <BeTextControl label="Name:" value={v} onChange={(e) => set(e.target.value)} />
 *   <BeTextView value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} />
 */

import type { ReactNode } from "react";
import { type BeProps, beComponent } from "./create.tsx";
import { pixelText } from "./pixel-text.tsx";
import type { BeGrowable } from "./Stack.tsx";
import type { BeTextFace } from "./Text.tsx";

export type BeTextControlProps = BeProps<
	"input",
	BeGrowable & {
		/** Optional label drawn to the left of the field. */
		label?: ReactNode;
		/** Font Face whole control, label included. */
		face?: BeTextFace;
	}
>;

/** The ref lands on the <input>, not the <label> -- see create.tsx. */
export const BeTextControl = beComponent<BeTextControlProps, HTMLInputElement>("BeTextControl", (props, be) => {
	const { label, face, grow, scale, className, style, type, ...rest } = props;
	const { ref, ...root } = be("be-text-field", grow && "be-grow", face && `be-text--${face}`);

	return (
		<label {...root}>
			{label != null && pixelText(label, "be-text-field__label")}
			<input type={type ?? "text"} className="be-text-field__input" ref={ref} {...rest} />
		</label>
	);
});

export type BeTextViewProps = BeProps<
	"textarea",
	{
		/** Opt back into the browser's vertical resize grip (off by default; R5 has none). */
		resizable?: boolean;
		/** Font Face of the text area. */
		face?: BeTextFace;
	}
>;

/** The ref lands on the <textarea>, not the wrapper -- see create.tsx. */
export const BeTextView = beComponent<BeTextViewProps, HTMLTextAreaElement>("BeTextView", (props, be) => {
	const { scale, className, style, resizable, face, ...rest } = props;
	const { ref, ...root } = be(
		"be-text-field",
		"be-text-field--multiline",
		resizable && "be-text-field--resizable",
		face && `be-text--${face}`,
	);

	return (
		<div {...root}>
			<textarea className="be-text-field__input" ref={ref} {...rest} />
		</div>
	);
});
